/**
 * Validation message codes and client-side translation helpers.
 */

const VALIDATION_KEY_PATTERN = /^(validation|forms)\.[A-Za-z0-9_.]+$/;

export function isValidationCatalogKey(value) {
  return typeof value === "string" && VALIDATION_KEY_PATTERN.test(value);
}

/**
 * @param {(key: string, params?: object) => string} t
 * @param {string|null|undefined} message
 * @param {object} [params]
 * @returns {string}
 */
export function translateValidationMessage(t, message, params) {
  if (!message) {
    return "";
  }
  if (isValidationCatalogKey(message)) {
    return t(message, params);
  }
  return message;
}

/**
 * @param {Record<string, string|null|undefined>} fieldErrors
 * @param {(key: string, params?: object) => string} t
 * @returns {Record<string, string>}
 */
export function translateFieldErrors(fieldErrors = {}, t) {
  const translated = {};
  Object.entries(fieldErrors).forEach(([field, message]) => {
    if (!message) {
      return;
    }
    translated[field] = translateValidationMessage(t, message);
  });
  return translated;
}

export const LEAD_VALIDATION_CODES = Object.freeze({
  buyerNameRequired: "validation.lead.buyerNameRequired",
  buyerNameTooShort: "validation.lead.buyerNameTooShort",
  buyerNameTooLong: "validation.lead.buyerNameTooLong",
  buyerNameInvalid: "validation.lead.buyerNameInvalid",
  buyerPhoneRequired: "validation.lead.buyerPhoneRequired",
  buyerPhoneInvalid: "validation.lead.buyerPhoneInvalid",
  buyerPhoneTooLong: "validation.lead.buyerPhoneTooLong",
  buyerEmailRequired: "validation.lead.buyerEmailRequired",
  buyerEmailInvalid: "validation.lead.buyerEmailInvalid",
  preferredContactInvalid: "validation.lead.preferredContactInvalid",
  messageRequired: "validation.lead.messageRequired",
  messageTooShort: "validation.lead.messageTooShort",
  messageTooLong: "validation.lead.messageTooLong",
  messageInvalid: "validation.lead.messageInvalid",
  consentRequired: "validation.lead.consentRequired",
  formBlocked: "validation.lead.formBlocked",
  formSpam: "validation.lead.formSpam",
});

export const SELLER_VALIDATION_CODES = Object.freeze({
  sellerTypeRequired: "validation.seller.sellerTypeRequired",
  sellerNameRequired: "validation.seller.sellerNameRequired",
  sellerNameTooShort: "validation.seller.sellerNameTooShort",
  sellerNameTooLong: "validation.seller.sellerNameTooLong",
  sellerNameInvalid: "validation.seller.sellerNameInvalid",
  sellerPhoneRequired: "validation.seller.sellerPhoneRequired",
  sellerPhoneInvalid: "validation.seller.sellerPhoneInvalid",
  sellerPhoneTooLong: "validation.seller.sellerPhoneTooLong",
  sellerEmailRequired: "validation.seller.sellerEmailRequired",
  sellerEmailInvalid: "validation.seller.sellerEmailInvalid",
  preferredContactInvalid: "validation.seller.preferredContactInvalid",
  cityRequired: "validation.seller.cityRequired",
  cityTooShort: "validation.seller.cityTooShort",
  cityTooLong: "validation.seller.cityTooLong",
  departmentTooLong: "validation.seller.departmentTooLong",
  vehicleMakeRequired: "validation.seller.vehicleMakeRequired",
  vehicleMakeTooShort: "validation.seller.vehicleMakeTooShort",
  vehicleMakeTooLong: "validation.seller.vehicleMakeTooLong",
  vehicleModelRequired: "validation.seller.vehicleModelRequired",
  vehicleModelTooLong: "validation.seller.vehicleModelTooLong",
  vehicleYearRequired: "validation.seller.vehicleYearRequired",
  vehicleYearTooOld: "validation.seller.vehicleYearTooOld",
  vehicleYearTooFuture: "validation.seller.vehicleYearTooFuture",
  vehicleTrimTooLong: "validation.seller.vehicleTrimTooLong",
  vehicleMileageRequired: "validation.seller.vehicleMileageRequired",
  vehicleMileageInvalid: "validation.seller.vehicleMileageInvalid",
  vehicleMileageTooHigh: "validation.seller.vehicleMileageTooHigh",
  askingPriceRequired: "validation.seller.askingPriceRequired",
  askingPriceInvalid: "validation.seller.askingPriceInvalid",
  askingPriceTooHigh: "validation.seller.askingPriceTooHigh",
  currencyInvalid: "validation.seller.currencyInvalid",
  ownershipStatusRequired: "validation.seller.ownershipStatusRequired",
  conditionSummaryTooLong: "validation.seller.conditionSummaryTooLong",
  conditionSummaryInvalid: "validation.seller.conditionSummaryInvalid",
  notesTooLong: "validation.seller.notesTooLong",
  plateTooLong: "validation.seller.plateTooLong",
  vinTooLong: "validation.seller.vinTooLong",
  consentRequired: "validation.seller.consentRequired",
  formSpam: "validation.seller.formSpam",
});

export const SELLER_RESULT_CODES = Object.freeze({
  received: "validation.sellerResult.received",
  validation_error: "validation.sellerResult.validation_error",
  duplicate: "validation.sellerResult.duplicate",
  spam_rejected: "validation.sellerResult.spam_rejected",
  rate_limited: "validation.sellerResult.rate_limited",
  service_error: "validation.sellerResult.service_error",
  feature_disabled: "validation.sellerResult.feature_disabled",
  network_error: "validation.sellerResult.network_error",
  unknown_error: "validation.sellerResult.unknown_error",
});
